import React from 'react';
import '../release.css';

/**
 * Why a title has nothing to play, and the one honest thing to watch instead.
 *
 * The catalog sends `title.release` for films (see releaseOf in the TMDB
 * provider): which stage of release the film is at, when it reached cinemas
 * and when a home release is expected. A film still in cinemas has no
 * release worth playing - cams are rejected outright - so the page says so
 * up front instead of letting Play search, fail and shrug.
 *
 * The trailer is always called a trailer. It opens on YouTube in a new tab:
 * the CSP allows no third-party frames (server.js), by the owner's choice.
 */

// UTC on both sides: a release date is a calendar day, and formatting it in
// local time put The Odyssey in cinemas a day early anywhere west of London
const dateFormat = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC'
});
let regionNames = null;
try {
  regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
} catch (err) {
  // Older engines: the date reads fine without the country
}

export const formatReleaseDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return dateFormat.format(new Date(Date.UTC(y, m - 1, d)));
};

const countryName = (code) => {
  if (!code) return '';
  try {
    const name = regionNames?.of(code) || code;
    return name === 'United States' ? 'the US' : name === 'United Kingdom' ? 'the UK' : name;
  } catch (err) {
    return code;
  }
};

/** A film that cannot have a home release yet: in cinemas or not out at all. */
export const isNotOut = (title) =>
  title?.type === 'movie' && ['theatrical', 'upcoming'].includes(title.release?.stage);

/** The kicker, headline and sentence for a film that is not out for home viewing. */
export const notOutCopy = (title) => {
  const { stage, theatrical, home } = title.release || {};
  const homeLine = home
    ? `${home.kind === 'Blu-ray & DVD' ? 'Blu-ray & DVD release' : 'Digital release'} expected ${formatReleaseDate(home.date)}${home.country ? ` in ${countryName(home.country)}` : ''}.`
    : 'No home release date has been announced yet.';

  if (stage === 'theatrical') {
    return {
      kicker: 'In theatres',
      headline: 'Not yet released for home viewing',
      body: `In cinemas since ${formatReleaseDate(theatrical?.date)}. ${homeLine}`,
      after: 'It becomes playable here once it is out.'
    };
  }
  return {
    kicker: 'Coming soon',
    headline: 'Not released yet',
    body: theatrical
      ? `In cinemas from ${formatReleaseDate(theatrical.date)}. ${homeLine}`
      : `Still being made, with no release date yet. ${home ? homeLine : ''}`.trim(),
    after: null
  };
};

export const trailerUrl = (trailer) => (trailer?.key ? `https://www.youtube.com/watch?v=${trailer.key}` : null);

/**
 * The trailer, labelled as one. `primary` is the white pill used when the
 * trailer is the best thing on offer; otherwise it is a glass button.
 */
export const TrailerButton = ({ trailer, primary = false, className = '' }) => {
  const href = trailerUrl(trailer);
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`rs-trailer${primary ? ' rs-trailer--primary' : ''} ${className}`.trim()}
      title={trailer.name ? `${trailer.name} — opens YouTube` : 'Opens YouTube'}
    >
      <span className={`play-tri${primary ? ' play-tri--dark' : ''}`} />
      Watch official trailer
      <span className="rs-tag">TRAILER</span>
    </a>
  );
};

/**
 * The status panel: shown before anyone presses Play for a film that is not
 * out, and after a search comes back empty or nothing found would start.
 *
 * kind: 'not-out' | 'none' | 'failed'
 */
const ReleaseStatus = ({ title, kind, scope, reason, onRetry, retrying = false }) => {
  let kicker;
  let headline;
  let body;
  let after = null;

  if (kind === 'not-out') {
    ({ kicker, headline, body, after } = notOutCopy(title));
    if (reason) after = reason;
  } else if (kind === 'none') {
    kicker = 'Nothing to play';
    headline = 'No release found';
    body = reason
      || `We searched every indexer we can reach and found no playable copy of this ${scope || 'title'}.`;
    after = 'New releases appear over time — try again later.';
  } else {
    kicker = 'Playback did not start';
    headline = 'Couldn’t start any release';
    body = reason || 'Every release we found failed to open.';
    after = 'The details below show what was tried.';
  }

  return (
    <section className={`rs rs--${kind}`} role="status">
      <div className="rs-kicker">
        <span className="rs-dot" />
        {kicker.toUpperCase()}
      </div>
      <h3 className="rs-headline">{headline}</h3>
      <p className="rs-body">{body}</p>
      {after && <p className="rs-after">{after}</p>}
      {(title?.trailer || onRetry) && (
        <div className="rs-actions">
          <TrailerButton trailer={title?.trailer} primary={kind === 'not-out' || kind === 'none'} />
          {onRetry && (
            <button type="button" className="rs-ghost" onClick={onRetry} disabled={retrying}>
              {retrying ? 'Searching…' : kind === 'not-out' ? 'Search for a release anyway' : 'Try again'}
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default ReleaseStatus;
